import {Button, Form, message} from "antd";
import PageHeader from "../common/page-header/page-header";
import React, {useContext, useEffect, useState} from "react";
import UserLoginContext from "../../context/userLoginContext";
import {Plus} from "lucide-react";
import server from "../../server";
import {IPoll, IPollResult} from "./types/poll.types";
import PollCreateModal from "./utils/poll-create-modal";
import PollDrawer from "./utils/poll-drawer";
import PollAdminView from "./poll-admin-view";
import PollEmployeeView from "./poll-employee-view";
import {webSocketService} from "../../services/WebSocketService";
import '../../styles/component/poll/poll.scss';

const Poll = () => {
    const {user} = useContext(UserLoginContext);
    const [polls, setPolls] = useState<IPoll[]>([]);
    const [loadingPolls, setLoadingPolls] = useState(true);
    const [open, setOpen] = useState({
        createPoll: false,
        drawer: false,
    });
    const [result, setResult] = useState<IPollResult>();
    const [pollForm] = Form.useForm();
    const [selectedPollId, setSelectedPollId] = useState<string | null>(null);
    const selectedPoll = polls.find(item => item.id === selectedPollId) || null;
    const [searchPayload, setSearchPayload] = useState({
        sortDirection: "DESC",
        active: true
    });

    useEffect(() => {
        if (!open.drawer || !selectedPollId) {
            return;
        }

        webSocketService.subscribeToTopic(
            "polls",
            `/topic/polls/${selectedPollId}`,
            async (message) => {
                try {
                    const liveResult: IPollResult = JSON.parse(message);

                    // Update selected poll in polls state (used for live results)
                    setPolls(prevPolls =>
                        prevPolls.map(poll =>
                            poll.id === liveResult.pollId
                                ? {
                                    ...poll,
                                    totalVotes: liveResult.totalVotes,
                                    options: poll.options.map(option => {
                                        const liveOption = liveResult.options.find(
                                            o => o.optionId === option.id
                                        );

                                        return liveOption
                                            ? {
                                                ...option,
                                                voteCount: liveOption.voteCount,
                                                votePercentage: liveOption.votePercentage,
                                                voters: liveOption.voters,
                                            }
                                            : option;
                                    }),
                                }
                                :
                                poll
                        )
                    )
                    ;
                } catch (error) {
                    console.error("Error processing websocket message", error);
                }
            }
        );

        return () => {
            webSocketService.unsubscribeTopic("polls");
        };
    }, [open.drawer, selectedPollId]);

    useEffect(() => {
        fetchPolls(searchPayload);
    }, []);

    async function fetchPolls(payload?: any) {
        setLoadingPolls(true);
        try {
            const response = await server.poll.search(payload);
            setPolls(response);
            return response;
        } catch (e) {
            console.log("Error while fetching polls", e);
            return [];
        } finally {
            setLoadingPolls(false);
        }
    }

    function handleDateChange(range: { startDate: string; endDate: string }) {
        const payload = {
            ...searchPayload,
            start: {
                startDate: range.startDate,
                endDate: range.endDate,
            },
        };
        fetchPolls(payload);
    }

    const handleToolbarFilter = (type: string) => {
        let payload: any = {
            sortDirection: "DESC"
        };

        switch (type) {
            case "ALL":
                payload.sortDirection = "DESC";
                break;

            case "POLL":
                payload.anonymous = false;
                break;

            case "VOTE":
                payload.anonymous = true;
                break;

            case "ACTIVE":
                payload.active = true;
                break;

            case "INACTIVE":
                payload.active = false;
                break;

            default:
                break;
        }

        setSearchPayload(payload);
        fetchPolls(payload);
    };

    async function handlePollSubmit() {
        try {
            const values = await pollForm.validateFields();
            const payload = {
                ...values,
                endTime: values.endTime.utc(),
                options: values?.options?.map((item: string) => ({
                    text: item
                }))
            };
            await server.poll.create(payload);
            fetchPolls();
            message.success("Poll created successfully!")
            pollForm.resetFields();
            setOpen((prev: any) => (
                {
                    ...prev,
                    createPoll: false
                }
            ))
        } catch (e) {
            console.log("Error while creating poll", e);
        }
    }

    const handleCreateModalClose = () => {
        pollForm.resetFields();
        setOpen((prev: any) => ({
            ...prev,
            createPoll: false
        }))
    }

    async function handleEndPoll(id: string) {
        try {
            await server.poll.end(id);
            const updatedPolls = await fetchPolls();
            const poll = updatedPolls.find((item: any) => item.id === id);
            if (poll?.resultDeclared) {
                const response = await server.poll.getResult(id);
                setResult(response);
            } else {
                setResult(undefined);
            }
            message.success("Poll ended successfully!");
        } catch (e) {
            console.log("Error while ending poll", e);
        }
    }

    async function handleDeclareResult(id: string) {
        try {
            await server.poll.declareResult(id);
            await fetchPolls();
            const response = await server.poll.getResult(id);
            setResult(response);
            message.success("Result declared successfully!");
        } catch (e) {
            console.log("Error while declaring result", e);
        }
    }

    async function handleAdminViewResult(pollId: string) {
        setSelectedPollId(pollId);
        try {
            const adminResult = await server.poll.getAdminResults(pollId);
            setResult(adminResult);
            setOpen(prev => ({
                ...prev,
                drawer: true,
            }));
        } catch (e) {
            console.log("Error while fetching result", e);
        }
    }

    async function handleViewResult(id: string) {
        try {
            const result = await server.poll.getResult(id);
            setResult(result);
            setSelectedPollId(id);
            setOpen((prev: any) => ({
                ...prev,
                drawer: true
            }))
        } catch (e) {
            console.log("Error while viewing result", e);
        }
    }

    const handleOnClick = async (id: string) => {
        const poll = polls.find(item => item.id === id);
        if (!poll) return;
        setSelectedPollId(id);
        if (poll.anonymous && poll.resultDeclared) {
            try {
                const response = await server.poll.getResult(id);
                setResult(response);
            } catch (e) {
                console.log("Error fetching result", e);
            }
        } else if (!poll.anonymous && !poll.active) {
            try {
                const response = await server.poll.getResult(id);
                setResult(response);
            } catch (e) {
                console.log("Error fetching result", e);
            }
        } else {
            setResult(undefined);
        }
        setOpen((prev: any) => ({
            ...prev,
            drawer: true
        }))
    };

    const handleDrawerClose = () => {
        setOpen((prev: any) => ({
            ...prev,
            drawer: false
        }))
        setSelectedPollId(null);
        setResult(undefined);
    }

    return (
        <>
            <PageHeader
                title={'Polls & Votes'}
                description={'Your voice matters—cast your vote to drive meaningful change'}
                actions={user?.isAdmin && <Button
                    type={'primary'}
                    icon={<Plus/>}
                    onClick={() => setOpen((prev: any) => ({...prev, createPoll: true}))}
                >
                    New Poll
                </Button>
                }
            />

            {
                user.isAdmin ?
                    <PollAdminView
                        polls={polls}
                        loadingPolls={loadingPolls}
                        fetchPolls={fetchPolls}
                        handleOnClick={handleOnClick}
                        onFilterChange={handleToolbarFilter}
                        handleDateChange={handleDateChange}
                    />
                    :
                    <PollEmployeeView
                        polls={polls}
                        fetchPolls={fetchPolls}
                        loadingPolls={loadingPolls}
                        handleViewResult={handleViewResult}
                        handleOnClick={handleOnClick}
                        onFilterChange={handleToolbarFilter}
                        handleDateChange={handleDateChange}
                    />
            }

            <PollCreateModal
                pollForm={pollForm}
                open={open.createPoll}
                handleModalClose={handleCreateModalClose}
                handlePollSubmit={handlePollSubmit}
            />

            <PollDrawer
                open={open.drawer}
                isAdmin={user.isAdmin}
                result={result}
                handleDrawerClose={handleDrawerClose}
                selectedPoll={selectedPoll}
                handleEndPoll={handleEndPoll}
                handleDeclareResult={handleDeclareResult}
                handleAdminViewResult={handleAdminViewResult}
            />
        </>
    )
}

export default Poll;
